// meeting-note-citations — resolve a call summary's markdown footnotes.
//
// ATLAS writes KB-note / aiSummary bodies as markdown that grounds each claim
// with an inline `[^N]` marker, then closes the body with a footnote-definition
// block whose definitions point at transcript line ids:
//
//     ...education funding[^4].
//
//     [^1]: bf1836a1-l2
//     [^4]: bf1836a1-l12, bf1836a1-l13
//
// A line id alone means nothing to a reader. Given the call's transcript lines,
// `prepareSummary` rewrites each definition to the verbatim quote it cites
// (`**Speaker** `mm:ss` — "text"`) so react-markdown + remark-gfm render
// readable footnotes, and strips any orphaned `[^N]` marker whose definition
// ATLAS dropped (older rows predate the mandatory-block prompt, atlas-v3).
//
// Pure and deterministic — safe in render paths.

export interface CitationSource {
    /** Transcript line id, e.g. `bf1836a1-l2`. */
    id: string;
    speaker: string;
    /** Display offset within the call, e.g. `0:13`. */
    timestamp: string;
    text: string;
}

export interface PreparedSummary {
    /** Markdown ready for react-markdown + remark-gfm. */
    markdown: string;
    /** Markers referenced in prose whose definition was missing (dropped from output). */
    orphanedMarkers: string[];
    /** Footnote definitions resolved to at least one transcript quote. */
    resolvedCount: number;
    /** True when the body referenced any `[^N]` marker at all. */
    hasCitations: boolean;
}

// A footnote definition line: `[^N]: <value>` at the start of a (trimmed) line.
const DEF_LINE_RE = /^\[\^([^\]\s]+)\]:[ \t]*(.*)$/;
// An inline footnote reference anywhere in prose.
const REF_RE = /\[\^([^\]\s]+)\]/g;

/** Backslash-escape the markdown control chars that could leak from a raw quote. */
function escapeMd(s: string): string {
    return s.replace(/([\\`*_[\]])/g, '\\$1');
}

/** Rewrite one definition value (comma-separated line ids) to resolved markdown. */
function resolveDef(
    rawValue: string,
    sourceById: Map<string, CitationSource>,
): { md: string; resolved: boolean } {
    const ids = rawValue
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    const parts: string[] = [];
    let anyResolved = false;
    for (const id of ids) {
        const src = sourceById.get(id);
        if (src) {
            anyResolved = true;
            parts.push(
                `**${escapeMd(src.speaker)}** \`${src.timestamp}\` — "${escapeMd(src.text)}"`,
            );
        } else {
            // Definition present but the id is not in this call's transcript —
            // keep the raw id (mono) rather than dropping the citation silently.
            parts.push(`\`${escapeMd(id)}\``);
        }
    }
    return { md: parts.join(' · '), resolved: anyResolved };
}

export function prepareSummary(
    body: string,
    sources: readonly CitationSource[] = [],
): PreparedSummary {
    const sourceById = new Map<string, CitationSource>();
    for (const s of sources) sourceById.set(s.id, s);

    // 1. Pull the definition block out of the body, line by line.
    const defs = new Map<string, string>();
    const proseLines: string[] = [];
    for (const line of body.split('\n')) {
        const m = line.trimStart().match(DEF_LINE_RE);
        if (m) {
            defs.set(m[1], m[2]);
        } else {
            proseLines.push(line);
        }
    }
    let prose = proseLines.join('\n');

    // 2. Walk the prose references: keep those with a definition, strip orphans.
    const referenced: string[] = [];
    const orphanedMarkers: string[] = [];
    prose = prose.replace(REF_RE, (raw, marker: string) => {
        if (!referenced.includes(marker)) referenced.push(marker);
        if (defs.has(marker)) return raw;
        if (!orphanedMarkers.includes(marker)) orphanedMarkers.push(marker);
        return '';
    });

    // A `---` rule used only to separate the (now removed) definition block from
    // the prose is left dangling — trim a trailing rule + whitespace.
    prose = prose.replace(/\n+[ \t]*-{3,}[ \t]*\s*$/, '').trimEnd();

    // 3. Rebuild a clean definition block for the markers actually referenced
    //    (in reference order, so remark-gfm numbers them as they appear).
    let resolvedCount = 0;
    const defBlock: string[] = [];
    for (const marker of referenced) {
        const value = defs.get(marker);
        if (value === undefined) continue;
        const { md, resolved } = resolveDef(value, sourceById);
        if (resolved) resolvedCount += 1;
        defBlock.push(`[^${marker}]: ${md}`);
    }

    const markdown = defBlock.length > 0 ? `${prose}\n\n${defBlock.join('\n')}\n` : prose;

    return {
        markdown,
        orphanedMarkers,
        resolvedCount,
        hasCitations: referenced.length > 0,
    };
}
